import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'

import { cn } from '@/lib/utils'

/**
 * Button component following Figma design system specifications
 * Based on 3-Componentes.json design tokens
 */
const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center whitespace-nowrap font-geist font-medium',
    'transition-colors focus-visible:outline-none focus-visible:ring-1',
    'focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0'
  ),
  {
    variants: {
      variant: {
        default: cn(
          'bg-slate-800 text-white shadow-sm',
          'hover:bg-slate-900 active:bg-slate-950'
        ),
        destructive: cn(
          'bg-red-600 text-white shadow-sm',
          'hover:bg-red-700 active:bg-red-800'
        ),
        outline: cn(
          'border border-gray-300 bg-white text-gray-700 shadow-sm',
          'hover:bg-gray-50 active:bg-gray-100'
        ),
        secondary: cn(
          'bg-gray-100 text-gray-900 shadow-sm',
          'hover:bg-gray-200 active:bg-gray-300'
        ),
        ghost: 'hover:bg-gray-100 hover:text-gray-900',
        link: 'text-blue-600 underline-offset-4 hover:underline',
      },
      size: {
        sm: cn(
          'h-8 gap-2 rounded-md px-3 text-xs leading-4 tracking-[0.005em]',
          '[&_svg]:h-3.5 [&_svg]:w-3.5'
        ),
        md: cn(
          'h-9 gap-2 rounded-md px-4 text-sm leading-5 tracking-[0.005em]',
          '[&_svg]:h-4 [&_svg]:w-4'
        ),
        lg: cn(
          'h-10 gap-2 rounded-md px-6 text-sm leading-5 tracking-[0.005em]',
          '[&_svg]:h-4 [&_svg]:w-4'
        ),
        xl: cn(
          'h-11 gap-2 rounded-md px-8 text-base leading-6 tracking-[0.005em]',
          '[&_svg]:h-5 [&_svg]:w-5'
        ),
        icon: 'h-9 w-9 rounded-md [&_svg]:h-4 [&_svg]:w-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      icon,
      iconPosition = 'left',
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {icon && iconPosition === 'left' && icon}
        {children}
        {icon && iconPosition === 'right' && icon}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
